
import axios from "axios";

const API_BASE_URL = "https://fortune-wheel-app-634744fe7f32.herokuapp.com";

const TOKEN_STORAGE_KEY_PREFIX = "fw_tokens_";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

function extractTelegramUserId(tgInitData) {
  if (!tgInitData) return null;
  try {
    const params = new URLSearchParams(tgInitData);
    const userJson = params.get("user");
    if (!userJson) return null;
    const user = JSON.parse(userJson);
    if (user && (typeof user.id === "number" || typeof user.id === "string")) {
      return String(user.id);
    }
  } catch (e) {
    console.warn("[wheelApi] Failed to parse tgInitData for user id", e);
  }
  return null;
}

function getTokenStorageKey(tgInitData) {
  const userId = extractTelegramUserId(tgInitData) || "anonymous";
  return `${TOKEN_STORAGE_KEY_PREFIX}${userId}`;
}

function setAuthHeader(accessToken, tokenType = "bearer") {
  if (!accessToken) {
    delete api.defaults.headers.common["Authorization"];
    return;
  }
  api.defaults.headers.common["Authorization"] = `${tokenType} ${accessToken}`;
}

function persistTokensRaw(tgInitData, tokens) {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        getTokenStorageKey(tgInitData),
        JSON.stringify(tokens)
      );
    } catch (e) {
      console.warn("[wheelApi] Failed to persist tokens (raw)", e);
    }
  }
}

function saveTokens(tgInitData, data) {
  const tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    tokenType: data.token_type || "bearer",
  };

  persistTokensRaw(tgInitData, tokens);
  setAuthHeader(tokens.accessToken, tokens.tokenType);
  return tokens;
}

function loadTokens(tgInitData) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(getTokenStorageKey(tgInitData));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.accessToken) return null;
    setAuthHeader(parsed.accessToken, parsed.tokenType || "bearer");
    return parsed;
  } catch (e) {
    console.warn("[wheelApi] Failed to load tokens", e);
    return null;
  }
}

function clearTokens(tgInitData) {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(getTokenStorageKey(tgInitData));
    } catch (e) {
      console.warn("[wheelApi] Failed to clear tokens", e);
    }
  }
  delete api.defaults.headers.common["Authorization"];
}

export function getStoredTokens(tgInitData) {
  return loadTokens(tgInitData);
}


async function getUser() {
  const res = await api.get("/api/v1/users");
  return res.data; 
}


async function registerAndStore(tgInitData) {
  const res = await api.post(
    "/api/v1/register",
    null,
    {
      params: { tg_init_data: tgInitData }, 
    }
  );

  const data = res.data;

  
  let payload = data;
  if (typeof data === "string") {
    try {
      payload = JSON.parse(data);
    } catch {
      console.warn("[wheelApi] register returned plain string, cannot parse");
      payload = {};
    }
  }

  const tokens = saveTokens(tgInitData, payload);
  const user = payload.user || null;

  console.log("[wheelApi] registerAndStore OK:", { user, tokens });

  return { tokens, user };
}


async function refreshAndStore(tgInitData) {
  const existing = loadTokens(tgInitData);
  if (!existing?.refreshToken) {
    throw new Error("[wheelApi] No refreshToken to refresh");
  }

  const res = await api.post(
    `/api/v1/refresh/token/${existing.refreshToken}`
  );

  const data = res.data;

  let tokens;
  if (typeof data === "string") {
    
    tokens = {
      ...existing,
      accessToken: data,
    };
  } else {
    tokens = {
      accessToken: data.access_token || existing.accessToken,
      refreshToken: data.refresh_token || existing.refreshToken,
      tokenType: data.token_type || existing.tokenType || "bearer",
    };
  }

  persistTokensRaw(tgInitData, tokens);
  setAuthHeader(tokens.accessToken, tokens.tokenType);

  console.log("[wheelApi] refreshAndStore OK");
  return tokens;
}

export async function ensureAuthAndGetUser(tgInitData) {
  let tokens = loadTokens(tgInitData);

  if (tokens?.accessToken) {
    
    try {
      const user = await getUser();
      return { user, tokens };
    } catch (err) {
      const status = err?.response?.status;
      console.warn(
        "[ensureAuthAndGetUser] getUser with stored token failed, status =",
        status
      );

      
      if (status === 401 || status === 403) {
        try {
          tokens = await refreshAndStore(tgInitData);
          const user = await getUser();
          return { user, tokens };
        } catch (e2) {
          console.warn("[ensureAuthAndGetUser] refresh failed:", e2);
          clearTokens(tgInitData);
        }
      } else if (status === 404) {
        
        clearTokens(tgInitData);
      } else {
        throw err;
      }
    }
  }

  
  const { user, tokens: newTokens } = await registerAndStore(tgInitData);
  return { user, tokens: newTokens };
}






async function fetchWheelConfig() {
  const res = await api.get("/api/v1/wheels");
  return res.data; 
}


export async function fetchWheelState(tgInitData) {
  const { user } = await ensureAuthAndGetUser(tgInitData);

  const wheel = await fetchWheelConfig();

  const now = Date.now();
  const nextSpinAt = user?.next_spin_at
    ? new Date(user.next_spin_at).getTime()
    : null;

  let remaining_ms = 0;
  if (nextSpinAt && nextSpinAt > now) {
    remaining_ms = nextSpinAt - now;
  }

  const rawStatus = wheel.status ?? wheel.wheel_status;
  const enabled =
    rawStatus === true ||
    rawStatus === "true" ||
    rawStatus === 1 ||
    rawStatus === "1";

  return {
    remaining_ms,
    status: enabled,
    prizes: wheel.wheel_prizes || [],
  };
}


export async function spinWheel(tgInitData) {
  await ensureAuthAndGetUser(tgInitData);

  try {
    const res = await api.post("/api/v1/users/spin", null);
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    console.warn("[spinWheel] first attempt failed, status =", status);

    if (status === 401 || status === 403) {
      
      await refreshAndStore(tgInitData);
      const retryRes = await api.post("/api/v1/users/spin", null);
      return retryRes.data;
    }

    throw err;
  }
}
